<script setup lang="ts">
import type { RichTextSection, Theme } from './report-typing';

import { useUpdatedReportStore } from '@src/store/report-store';
import { computed } from 'vue';

import SelectOptions from '../SelectOptions.vue';

const props = defineProps<{
  section: RichTextSection;
}>();

defineEmits<{
  action: [actionId: string];
}>();

const reportStore = useUpdatedReportStore();
const theme = computed<Theme | null>(() => {
  const loadedTemplate = reportStore.getTemplate();
  return loadedTemplate?.theme ?? null;
});

const currentValue = computed(() =>
  reportStore.getValue(props.section.field.key),
);

const textInputValue = computed(() => {
  return typeof currentValue.value === 'string' ? currentValue.value : '';
});

const isDisabled = computed(() => {
  const storeState = reportStore.values?.[props.section.field.key];
  if (storeState?.isDisabled !== undefined) return storeState.isDisabled;
  return props.section.field.isDisabled ?? false;
});

const isEditable = computed(() => {
  const storeState = reportStore.values?.[props.section.field.key];
  if (storeState?.isEditable !== undefined) return storeState.isEditable;
  return props.section.field.isEditable ?? true;
});

const headerStyle = computed(() => {
  if (!theme.value) return {};
  return {
    borderBottom: `1px solid ${theme.value.colors.border}`,
    paddingBottom: '4px',
    marginBottom: `${theme.value.spacing.fieldGap / 2}px`,
  };
});

const titleStyle = computed(() => {
  if (!theme.value) return {};
  const sl = theme.value.fonts.sectionLabel;
  return {
    fontFamily: sl.family,
    fontSize: `${sl.size}px`,
    fontWeight: sl.weight,
    color: theme.value.colors.text,
    textTransform: sl.uppercase ? 'uppercase' : 'none',
    letterSpacing: sl.letterSpacing ? `${sl.letterSpacing}px` : undefined,
  };
});

// const actionBtnStyle = computed(() => {
//   if (!theme.value) return {};
//   return {
//     fontFamily: theme.value.fonts.label.family,
//     fontSize: `${theme.value.fonts.label.size}px`,
//     color: theme.value.colors.accent,
//     borderColor: theme.value.colors.accent,
//     borderRadius: `${theme.value.borderRadius}px`,
//   };
// });

const textareaStyle = computed(() => {
  if (!theme.value) return {};
  // Set max height based on theme spacing (e.g. 5 lines of text)
  const lineHeight = theme.value.fonts.value.size * 1.4; // Approximate line height
  const maxHeight = lineHeight * 10 + 12; //
  return {
    fontFamily: theme.value.fonts.body.family,
    fontSize: `${theme.value.fonts.body.size}px`,
    fontWeight: theme.value.fonts.body.weight,
    color: theme.value.colors.text,
    borderColor: theme.value.colors.border,
    borderRadius: `${theme.value.borderRadius}px`,
    maxHeight: `${maxHeight}px`,
    minHeight: `${lineHeight * 9}px`,
  };
});

function onInput(e: Event) {
  const target = e.target as HTMLTextAreaElement;
  reportStore.setValue(props.section.field.key, target.value);
}
</script>

<template>
  <div class="rich-text-section">
    <!-- Title bar with optional actions -->
    <div class="rich-text-header" :style="headerStyle">
      <span class="rich-text-title" :style="titleStyle">
        {{ section.title }}
      </span>
    </div>

    <!-- Editable textarea -->
    <div class="textarea-wrapper" style="position: relative; width: 100%">
      <SelectOptions
        v-if="section.cannedTextGroups && section.cannedTextGroups.length > 0"
        :editable="isEditable && !isDisabled"
        :canned-text-groups="section.cannedTextGroups"
        :field-key="section.field.key"
      />
      <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
      <textarea
        class="rich-text-area"
        :style="textareaStyle"
        :value="textInputValue"
        :placeholder="section.field.placeholder || 'Type here...'"
        :disabled="isDisabled"
        :readonly="!isEditable"
        rows="4"
        @input="onInput"
      />
    </div>
  </div>
</template>

<style scoped>
.rich-text-section {
  display: flex;
  flex-direction: column;
}

.rich-text-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.rich-text-title {
  font-size: 11px;
  font-weight: 600;
}

.rich-text-actions {
  display: flex;
  gap: 6px;
}

.rich-text-action-btn {
  background: transparent;
  border: 1px solid;
  padding: 2px 8px;
  cursor: pointer;
  font-size: 9px;
  transition: opacity 0.15s;
}

.rich-text-action-btn:hover {
  opacity: 0.75;
}

.rich-text-area {
  width: 100%;
  border: 1px solid #ddd;
  background: #fff;
  padding: 6px 8px;
  font-size: 10px;
  resize: vertical;
  min-height: 60px;
  outline: none;
  box-sizing: border-box;
}

.rich-text-area:focus {
  border-color: #0284c7;
}

.rich-text-area[readonly] {
  background: #fafafa;
  cursor: default;
}
</style>
